import { InputState } from './DesktopInput';
import { VehicleTelemetry } from '../vehicle/VehiclePhysics';
import { TransmissionType } from '../vehicle/Powertrain';

export type MobileSteeringMode = 'slider' | 'arrows' | 'wheel' | 'gyro';
export type MobileHandedness = 'right' | 'left';
export type MobileButtonScale = 'normal' | 'large';

export class MobileTouchHUD {
  public container: HTMLElement;
  public state: InputState = {
    throttle: 0,
    brake: 0,
    steering: 0,
    handbrake: true,
    clutch: 0,
    blinker: 'off',
    shoulderCheck: null,
    mirrorLook: null,
    cameraToggle: false,
    horn: false,
    engineRestart: false,
    seatbeltToggle: false,
    seatbeltFastened: false,
    gearShiftUp: false,
    gearShiftDown: false,
    gearRequested: null,
    adjustCockpitToggle: false,
  };

  // Steering Mode Configuration (Default is Self-Centering Slider)
  public steeringMode: MobileSteeringMode = 'slider';
  public handedness: MobileHandedness = 'right';
  public buttonScale: MobileButtonScale = 'normal';
  public hapticEnabled: boolean = true;
  public useGyroSteering: boolean = false;

  // Steering Slider internal state
  public sliderTrackEl: HTMLElement | null = null;
  public sliderThumbEl: HTMLElement | null = null;
  private sliderTouchId: number | null = null;
  private sliderCenter: number = 0;
  private sliderMaxTravel: number = 56;
  private sliderSteerTarget: number = 0;

  // Arrow Steering internal state (Maintained for backward compatibility and test stability)
  private arrowSteerTarget: number = 0;
  private arrowSteerHoldDuration: number = 0;
  private rawSteering: number = 0;
  private lastArrowSteerTarget: number = 0;
  private arrowTouchLeftId: number | null = null;
  private arrowTouchRightId: number | null = null;

  // Steering Wheel internal state
  private wheelElement!: HTMLElement;
  private wheelTouchId: number | null = null;
  private wheelStartAngle: number = 0;
  private wheelCurrentRotation: number = 0; // -160 to 160 deg
  private wheelCenter = { x: 0, y: 0 };

  // Gyro Tilt internal state
  private gyroZeroAngle: number = 0;
  private gyroCurrentAngle: number = 0;
  private filteredTilt: number = 0;
  private gyroIndicatorEl: HTMLElement | null = null;

  // Control Containers
  private steerControlsContainer!: HTMLElement;
  private leftThumbCluster!: HTMLElement;
  private rightThumbCluster!: HTMLElement;
  private bottomControlsBar!: HTMLElement;

  // Buttons & Labels
  public btnArrowLeft: HTMLElement | null = null;
  public btnArrowRight: HTMLElement | null = null;
  private btnEngine!: HTMLElement;
  private btnSeatbelt!: HTMLElement;
  private btnHandbrake!: HTMLElement;
  private btnQuickGear!: HTMLElement;
  private quickGearTextEl!: HTMLElement;
  private btnSteerMode!: HTMLElement;
  private btnCamera!: HTMLElement;
  private clutchPedalEl: HTMLElement | null = null;
  private gasPedalEl!: HTMLElement;
  private brakePedalEl!: HTMLElement;
  private rotateOverlay!: HTMLElement;

  private currentTransmission: TransmissionType = 'automatic';
  private currentGearName: string = 'P';

  // External Callbacks
  private onOpenCockpitAdjusterCallback: (() => void) | null = null;
  private onToggleChecklistCallback: (() => void) | null = null;
  private onToggleHandbrakeCallback: ((latched: boolean) => void) | null = null;
  private onQuickStartCallback: (() => void) | null = null;

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'mobile-touch-hud';
    this.container.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      pointer-events: none;
      z-index: 35;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      user-select: none;
      -webkit-user-select: none;
      touch-action: none;
      box-sizing: border-box;
    `;

    this.loadSavedPreferences();
    this.injectStyles();
    this.buildHUD();
    this.setupGyro();
  }

  private loadSavedPreferences() {
    try {
      if (typeof localStorage !== 'undefined') {
        const savedMode = localStorage.getItem('fahrschule_mobile_steer_mode');
        if (savedMode === 'slider' || savedMode === 'arrows' || savedMode === 'wheel' || savedMode === 'gyro') {
          this.steeringMode = savedMode;
          this.useGyroSteering = savedMode === 'gyro';
        }
        const savedHandedness = localStorage.getItem('fahrschule_mobile_handedness');
        if (savedHandedness === 'right' || savedHandedness === 'left') {
          this.handedness = savedHandedness;
        }
        const savedScale = localStorage.getItem('fahrschule_mobile_btn_scale');
        if (savedScale === 'normal' || savedScale === 'large') {
          this.buttonScale = savedScale;
        }
        const savedHaptic = localStorage.getItem('fahrschule_mobile_haptic');
        if (savedHaptic !== null) {
          this.hapticEnabled = savedHaptic === 'true';
        }
      }
    } catch {
      // fallback to defaults
    }
  }

  public savePreferences() {
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('fahrschule_mobile_steer_mode', this.steeringMode);
        localStorage.setItem('fahrschule_mobile_handedness', this.handedness);
        localStorage.setItem('fahrschule_mobile_btn_scale', this.buttonScale);
        localStorage.setItem('fahrschule_mobile_haptic', String(this.hapticEnabled));
      }
    } catch {
      // ignore
    }
  }

  public triggerHaptic(durationMs: number = 15) {
    if (!this.hapticEnabled) return;
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try {
        navigator.vibrate(durationMs);
      } catch {
        // ignore
      }
    }
  }

  public onBlinkerChange: ((dir: 'left' | 'right' | 'hazard' | 'off') => void) | null = null;

  public setBlinker(dir: 'left' | 'right' | 'hazard' | 'off') {
    if (this.state.blinker === dir) return;
    this.state.blinker = dir;
    if (this.onBlinkerChange) {
      this.onBlinkerChange(this.state.blinker);
    }
  }

  public toggleBlinker(dir: 'left' | 'right' | 'hazard') {
    this.state.blinker = this.state.blinker === dir ? 'off' : dir;
    if (this.onBlinkerChange) {
      this.onBlinkerChange(this.state.blinker);
    }
  }

  public setHandbrake(latched: boolean) {
    this.state.handbrake = latched;
    this.updateHandbrakeButtonUI(latched);
  }

  public onToggleHandbrake(cb: (latched: boolean) => void) {
    this.onToggleHandbrakeCallback = cb;
  }

  public onOpenCockpitAdjuster(cb: () => void) {
    this.onOpenCockpitAdjusterCallback = cb;
  }

  public onToggleChecklist(cb: () => void) {
    this.onToggleChecklistCallback = cb;
  }

  public onQuickStart(cb: () => void) {
    this.onQuickStartCallback = cb;
  }

  private injectStyles() {
    if (document.getElementById('mobile-touch-hud-v2-css')) return;
    const style = document.createElement('style');
    style.id = 'mobile-touch-hud-v2-css';
    style.textContent = `
      /* Responsive Mobile Touch HUD V2 Styles */
      #mobile-touch-hud {
        padding: env(safe-area-inset-top, 0px) env(safe-area-inset-right, 10px) env(safe-area-inset-bottom, 8px) env(safe-area-inset-left, 10px);
      }
      .touch-pedal, .touch-btn-action, .touch-steer-btn, .touch-blinker-btn {
        touch-action: none !important;
        -webkit-tap-highlight-color: transparent;
        user-select: none !important;
        -webkit-user-select: none !important;
      }
      .touch-pedal:active, .touch-btn-action:active, .touch-steer-btn:active, .touch-blinker-btn:active {
        transform: scale(0.95);
        filter: brightness(1.25);
      }
      .touch-pedal-pressed {
        transform: scale(0.94) !important;
        filter: brightness(1.3) contrast(1.1) !important;
      }
      .touch-steer-pressed {
        background: rgba(14, 165, 233, 0.5) !important;
        border-color: #38bdf8 !important;
        color: #ffffff !important;
        transform: scale(0.94) !important;
      }
      @media (orientation: portrait) {
        #mobile-rotate-overlay:not(.dismissed) {
          display: flex !important;
          z-index: 999999 !important;
        }
        #mobile-rotate-overlay.dismissed {
          display: none !important;
        }
        /* Ergonomic Portrait Mode Mobile Adaptations */
        #touch-top-bar {
          padding: 2px 4px !important;
          gap: 3px !important;
        }
        #touch-top-bar .touch-btn-action {
          padding: 4px 6px !important;
          font-size: 10px !important;
          border-radius: 8px !important;
          gap: 2px !important;
        }
        #touch-mid-section {
          padding: 0 4px !important;
        }
        #touch-mid-section .touch-btn-action {
          padding: 5px 8px !important;
          font-size: 9.5px !important;
        }
        #touch-bottom-controls {
          padding: 0 6px 4px 6px !important;
          gap: 4px !important;
        }
        #touch-left-cluster {
          max-width: 145px !important;
          gap: 4px !important;
        }
        #touch-right-cluster {
          max-width: 155px !important;
          gap: 4px !important;
        }
        #touch-blinker-row {
          gap: 2px !important;
        }
        .touch-blinker-btn {
          padding: 4px 6px !important;
          font-size: 9px !important;
        }
        .touch-pedal {
          height: 105px !important;
        }
        #touch-btn-gas {
          width: 56px !important;
        }
        #touch-btn-brake {
          width: 60px !important;
        }
        #touch-btn-handbrake {
          padding: 4px 8px !important;
          font-size: 10px !important;
        }
      }
      @media (max-width: 950px), (max-height: 550px) {
        #touch-top-bar {
          padding: 2px 6px !important;
          gap: 4px !important;
        }
        #touch-top-left-group {
          max-width: 112px !important;
          gap: 4px !important;
        }
        #touch-top-bar .touch-btn-action {
          padding: 4px 6px !important;
          font-size: 14px !important;
          border-radius: 8px !important;
          min-width: 32px !important;
          max-width: 34px !important;
          height: 32px !important;
          box-sizing: border-box !important;
          justify-content: center !important;
          gap: 0px !important;
        }
        #touch-top-bar .touch-btn-action span:nth-child(2) {
          display: none !important;
        }
      }
      @media (orientation: landscape) {
        #mobile-rotate-overlay {
          display: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  private buildHUD() {
    this.container.innerHTML = '';

    // 0. Fullscreen / Landscape Rotation Overlay (Shown when user holds phone in portrait)
    this.buildRotateOverlay();

    // 1. Top Bar: Quick Camera, Motor, Gurt, Steuerung & Cockpit
    this.buildTopBar();

    // 2. Middle Section: Wing Buttons (Shoulder Check Links / Rechts)
    this.buildMidSection();

    // 3. Bottom Controls Area (Left Thumb: Steering & Blinkers; Right Thumb: Gas, Brake, Handbrake, Gear)
    this.buildBottomControls();
  }

  private buildRotateOverlay() {
    this.rotateOverlay = document.createElement('div');
    this.rotateOverlay.id = 'mobile-rotate-overlay';

    // Check if user already dismissed it previously in this session
    const alreadyDismissed = typeof sessionStorage !== 'undefined' && sessionStorage.getItem('fahrschule_rotate_dismissed') === 'true';
    if (alreadyDismissed) {
      this.rotateOverlay.classList.add('dismissed');
    }

    this.rotateOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(10, 15, 28, 0.88);
      backdrop-filter: blur(14px);
      -webkit-backdrop-filter: blur(14px);
      z-index: 10000;
      display: none;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 16px;
      padding: 20px;
      box-sizing: border-box;
      pointer-events: auto;
      text-align: center;
    `;

    const card = document.createElement('div');
    card.style.cssText = `
      position: relative;
      background: rgba(30, 41, 59, 0.94);
      border: 1.5px solid rgba(56, 189, 248, 0.35);
      border-radius: 24px;
      padding: 26px 20px 20px 20px;
      max-width: 330px;
      width: 90%;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 14px;
      box-shadow: 0 16px 40px rgba(0, 0, 0, 0.65);
    `;

    card.innerHTML = `
      <div style="font-size: 46px; animation: pulse 1.8s infinite ease-in-out;">🔄</div>
      <div style="font-size: 20px; font-weight: 800; color: #f8fafc; font-family: 'Chakra Petch', sans-serif;">
        Querformat empfohlen
      </div>
      <div style="font-size: 13px; color: #94a3b8; max-width: 280px; line-height: 1.45;">
        Bitte drehe dein Smartphone ins <b>Querformat (Landscape)</b> für die beste Sicht auf Straße und Spiegel.
      </div>
    `;

    // Close X button in top right of card
    const btnCloseCorner = document.createElement('button');
    btnCloseCorner.innerHTML = '✕';
    btnCloseCorner.title = 'Schließen';
    btnCloseCorner.style.cssText = `
      position: absolute;
      top: 12px;
      right: 12px;
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.10);
      border: 1px solid rgba(255, 255, 255, 0.20);
      color: #cbd5e1;
      font-size: 16px;
      font-weight: 700;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      touch-action: manipulation;
    `;

    const btnRotateAction = document.createElement('button');
    btnRotateAction.innerHTML = '📲 Vollbild &amp; Querformat aktivieren';
    btnRotateAction.style.cssText = `
      background: linear-gradient(135deg, #0284c7 0%, #2563eb 100%);
      color: white;
      border: 2px solid #38bdf8;
      border-radius: 24px;
      padding: 12px 20px;
      font-size: 13.5px;
      font-weight: 800;
      cursor: pointer;
      box-shadow: 0 8px 24px rgba(2, 132, 199, 0.5);
      touch-action: manipulation;
      width: 100%;
    `;

    const btnDismissPortrait = document.createElement('button');
    btnDismissPortrait.innerHTML = '✕ Im Hochformat weiterspielen';
    btnDismissPortrait.style.cssText = `
      background: rgba(255, 255, 255, 0.06);
      color: #cbd5e1;
      border: 1px solid rgba(255, 255, 255, 0.15);
      border-radius: 18px;
      padding: 10px 18px;
      font-size: 12.5px;
      font-weight: 700;
      cursor: pointer;
      touch-action: manipulation;
      width: 100%;
    `;

    const dismissOverlay = (e?: Event) => {
      if (e) {
        if (typeof (e as any).preventDefault === 'function') {
          (e as any).preventDefault();
        }
        if (typeof (e as any).stopPropagation === 'function') {
          (e as any).stopPropagation();
        }
      }
      this.rotateOverlay.classList.add('dismissed');
      this.rotateOverlay.style.display = 'none';
      try {
        sessionStorage.setItem('fahrschule_rotate_dismissed', 'true');
      } catch {}
    };

    btnCloseCorner.addEventListener('pointerdown', dismissOverlay);
    btnDismissPortrait.addEventListener('pointerdown', dismissOverlay);
    btnDismissPortrait.addEventListener('click', dismissOverlay);

    // Tap outside card to dismiss as well
    this.rotateOverlay.addEventListener('pointerdown', (e) => {
      if (e.target === this.rotateOverlay) {
        dismissOverlay(e);
      }
    });

    btnRotateAction.addEventListener('pointerdown', async (e) => {
      e.preventDefault();
      await this.requestLandscapeFullscreen();
      dismissOverlay();
    });

    card.appendChild(btnCloseCorner);
    card.appendChild(btnRotateAction);
    card.appendChild(btnDismissPortrait);
    this.rotateOverlay.appendChild(card);
    if (typeof document !== 'undefined' && document.body) {
      document.body.appendChild(this.rotateOverlay);
    } else {
      this.container.appendChild(this.rotateOverlay);
    }
  }

  public async requestLandscapeFullscreen() {
    try {
      const docEl = document.documentElement as any;
      if (!document.fullscreenElement) {
        if (docEl.requestFullscreen) {
          await docEl.requestFullscreen().catch(() => {});
        } else if (docEl.webkitRequestFullscreen) {
          await docEl.webkitRequestFullscreen().catch(() => {});
        } else if (docEl.mozRequestFullScreen) {
          await docEl.mozRequestFullScreen().catch(() => {});
        } else if (docEl.msRequestFullscreen) {
          await docEl.msRequestFullscreen().catch(() => {});
        }
      }
      if (screen.orientation && (screen.orientation as any).lock) {
        await (screen.orientation as any).lock('landscape').catch(() => {});
      } else if ((screen as any).lockOrientation) {
        (screen as any).lockOrientation('landscape');
      }
    } catch (err) {
      console.log('Orientation lock:', err);
    }
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 250);
  }

  private buildTopBar() {
    const topBar = document.createElement('div');
    topBar.id = 'touch-top-bar';
    topBar.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 4px 8px;
      pointer-events: none;
      gap: 6px;
      width: 100%;
      box-sizing: border-box;
      z-index: 25;
    `;

    const pillStyle = `
      background: rgba(15, 23, 42, 0.88);
      color: #f8fafc;
      border: 1px solid rgba(255, 255, 255, 0.18);
      padding: 6px 10px;
      border-radius: 10px;
      font-size: 11px;
      font-weight: 700;
      cursor: pointer;
      touch-action: manipulation;
      display: flex;
      align-items: center;
      gap: 5px;
      white-space: nowrap;
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
    `;

    // A. Left Top Group: Fullscreen, Camera Switcher, Steering Mode
    const leftTop = document.createElement('div');
    leftTop.id = 'touch-top-left-group';
    leftTop.style.cssText = `display: flex; gap: 5px; align-items: center; pointer-events: auto;`;

    // 1. Fullscreen Toggle Button
    const btnFullscreen = document.createElement('button');
    btnFullscreen.className = 'touch-btn-action';
    btnFullscreen.innerHTML = '<span>⛶</span><span>Vollbild</span>';
    btnFullscreen.style.cssText = pillStyle + ' color: #38bdf8; border-color: rgba(56, 189, 248, 0.4);';
    btnFullscreen.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(15);
      this.requestLandscapeFullscreen();
    });

    // 2. Camera Switcher Button
    this.btnCamera = document.createElement('button');
    this.btnCamera.className = 'touch-btn-action';
    this.btnCamera.innerHTML = '<span>🎥</span><span>Kamera [V]</span>';
    this.btnCamera.style.cssText = pillStyle + ' background: #1e3a8a; border-color: #3b82f6;';
    this.btnCamera.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(15);
      this.state.cameraToggle = true;
    });

    // 3. Quick Steering Mode Switcher (Slider / Wheel / Gyro / Arrows)
    this.btnSteerMode = document.createElement('button');
    this.btnSteerMode.className = 'touch-btn-action';
    this.btnSteerMode.style.cssText = pillStyle + ' color: #a78bfa; border-color: rgba(167, 139, 250, 0.4);';
    this.updateSteerModeButtonText();
    this.btnSteerMode.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(18);
      this.cycleSteeringMode();
    });

    leftTop.appendChild(btnFullscreen);
    leftTop.appendChild(this.btnCamera);
    leftTop.appendChild(this.btnSteerMode);

    // B. Right Top Group: Engine, Seatbelt, Cockpit Adjuster
    const rightTop = document.createElement('div');
    rightTop.id = 'touch-top-right-group';
    rightTop.style.cssText = `display: flex; gap: 5px; align-items: center; pointer-events: auto;`;

    // Engine Start/Stop Button
    this.btnEngine = document.createElement('button');
    this.btnEngine.id = 'touch-btn-engine';
    this.btnEngine.className = 'touch-btn-action';
    this.btnEngine.innerHTML = '<span>🔑</span><span>Motor [I]</span>';
    this.btnEngine.style.cssText = pillStyle + ' background: #b45309; border-color: #f59e0b;';
    this.btnEngine.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(25);
      this.state.engineRestart = true;
    });

    // Seatbelt Buckle/Unbuckle Button
    this.btnSeatbelt = document.createElement('button');
    this.btnSeatbelt.id = 'touch-btn-seatbelt';
    this.btnSeatbelt.className = 'touch-btn-action';
    this.btnSeatbelt.innerHTML = '<span>🔒</span><span>Gurt [B]</span>';
    this.btnSeatbelt.style.cssText = pillStyle + ' background: #991b1b; border-color: #ef4444;';
    this.btnSeatbelt.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(20);
      this.state.seatbeltToggle = true;
    });

    // In-Cockpit Adjuster Button
    const btnAdjuster = document.createElement('button');
    btnAdjuster.className = 'touch-btn-action';
    btnAdjuster.innerHTML = '<span>🪑</span><span>Sitz [J]</span>';
    btnAdjuster.title = 'Sitz & Spiegel einstellen [J]';
    btnAdjuster.style.cssText = pillStyle + ' background: #312e81; border-color: #6366f1;';
    btnAdjuster.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(15);
      if (this.onOpenCockpitAdjusterCallback) {
        this.onOpenCockpitAdjusterCallback();
      } else {
        this.state.adjustCockpitToggle = true;
      }
    });

    // Abfahrkontrolle Checklist Reopen Button
    const btnChecklist = document.createElement('button');
    btnChecklist.className = 'touch-btn-action';
    btnChecklist.id = 'touch-btn-checklist';
    btnChecklist.innerHTML = '<span>📋</span><span>Check [K]</span>';
    btnChecklist.title = 'Abfahrkontrolle ein-/ausblenden [K]';
    btnChecklist.style.cssText = pillStyle + ' background: #1e293b; border-color: #3b82f6;';
    btnChecklist.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(15);
      if (this.onToggleChecklistCallback) {
        this.onToggleChecklistCallback();
      }
    });

    rightTop.appendChild(this.btnEngine);
    rightTop.appendChild(this.btnSeatbelt);
    rightTop.appendChild(btnAdjuster);
    rightTop.appendChild(btnChecklist);

    topBar.appendChild(leftTop);
    topBar.appendChild(rightTop);
    this.container.appendChild(topBar);
  }

  private cycleSteeringMode() {
    if (this.steeringMode === 'slider') {
      this.setSteeringMode('wheel');
    } else if (this.steeringMode === 'wheel') {
      this.setSteeringMode('gyro');
    } else if (this.steeringMode === 'gyro') {
      this.setSteeringMode('arrows');
    } else {
      this.setSteeringMode('slider');
    }
  }

  public setSteeringMode(mode: MobileSteeringMode) {
    this.steeringMode = mode;
    this.useGyroSteering = mode === 'gyro';
    this.savePreferences();
    this.updateSteerModeButtonText();
    this.renderSteeringControls();
  }

  private updateSteerModeButtonText() {
    if (!this.btnSteerMode) return;
    if (this.steeringMode === 'slider') {
      this.btnSteerMode.innerHTML = '<span>↔️</span><span>Slider</span>';
    } else if (this.steeringMode === 'arrows') {
      this.btnSteerMode.innerHTML = '<span>◀▶</span><span>Tasten</span>';
    } else if (this.steeringMode === 'wheel') {
      this.btnSteerMode.innerHTML = '<span>🚗</span><span>Lenkrad</span>';
    } else {
      this.btnSteerMode.innerHTML = '<span>📱</span><span>Neigung</span>';
    }
  }

  private buildMidSection() {
    const midArea = document.createElement('div');
    midArea.id = 'touch-mid-section';
    midArea.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding: 0 8px;
      pointer-events: none;
      box-sizing: border-box;
      z-index: 22;
    `;

    const wingStyle = `
      background: rgba(15, 23, 42, 0.72);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      color: #38bdf8;
      border: 1.5px solid rgba(56, 189, 248, 0.5);
      padding: 7px 11px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 800;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 2px;
      touch-action: none;
      box-shadow: 0 4px 16px rgba(0,0,0,0.4);
      pointer-events: auto;
      cursor: pointer;
    `;

    // Left Shoulder Check Button
    const btnShoulderL = document.createElement('button');
    btnShoulderL.className = 'touch-btn-action';
    btnShoulderL.innerHTML = '<span>👀 Schulter [Z]</span><span style="font-size:9px;color:#94a3b8">LINKS</span>';
    btnShoulderL.style.cssText = wingStyle;
    btnShoulderL.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(12);
      this.state.shoulderCheck = 'left';
    });
    const releaseShoulderL = () => (this.state.shoulderCheck = null);
    btnShoulderL.addEventListener('pointerup', releaseShoulderL);
    btnShoulderL.addEventListener('pointercancel', releaseShoulderL);

    // Right Shoulder Check Button
    const btnShoulderR = document.createElement('button');
    btnShoulderR.className = 'touch-btn-action';
    btnShoulderR.innerHTML = '<span>👀 Schulter [X]</span><span style="font-size:9px;color:#94a3b8">RECHTS</span>';
    btnShoulderR.style.cssText = wingStyle;
    btnShoulderR.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(12);
      this.state.shoulderCheck = 'right';
    });
    const releaseShoulderR = () => (this.state.shoulderCheck = null);
    btnShoulderR.addEventListener('pointerup', releaseShoulderR);
    btnShoulderR.addEventListener('pointercancel', releaseShoulderR);

    midArea.appendChild(btnShoulderL);
    midArea.appendChild(btnShoulderR);
    this.container.appendChild(midArea);
  }

  private buildBottomControls() {
    this.bottomControlsBar = document.createElement('div');
    this.bottomControlsBar.id = 'touch-bottom-controls';
    this.bottomControlsBar.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      padding: 0 10px 6px 10px;
      pointer-events: none;
      gap: 12px;
      width: 100%;
      box-sizing: border-box;
      z-index: 25;
    `;

    // ── LEFT CLUSTER: Steering & Blinkers (Standard Right-Handed Setup) ──
    this.leftThumbCluster = document.createElement('div');
    this.leftThumbCluster.id = 'touch-left-cluster';
    this.leftThumbCluster.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      gap: 6px;
      pointer-events: auto;
    `;

    // Blinker Row & Horn
    const blinkerRow = document.createElement('div');
    blinkerRow.id = 'touch-blinker-row';
    blinkerRow.style.cssText = `display: flex; gap: 5px; align-items: center;`;

    const blinkStyle = `
      background: rgba(15, 23, 42, 0.85);
      border: 1.5px solid #334155;
      color: #f59e0b;
      padding: 6px 10px;
      border-radius: 8px;
      font-weight: 800;
      font-size: 11px;
      touch-action: manipulation;
      cursor: pointer;
    `;

    const btnBlinkL = document.createElement('button');
    btnBlinkL.className = 'touch-blinker-btn';
    btnBlinkL.innerText = '⬅️ Blinker [Q]';
    btnBlinkL.style.cssText = blinkStyle;
    btnBlinkL.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(15);
      this.toggleBlinker('left');
    });

    const btnHazard = document.createElement('button');
    btnHazard.className = 'touch-blinker-btn';
    btnHazard.innerText = '⚠️ Warn [F]';
    btnHazard.style.cssText = blinkStyle + ' color: #ef4444; border-color: #7f1d1d;';
    btnHazard.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(15);
      this.toggleBlinker('hazard');
    });

    const btnBlinkR = document.createElement('button');
    btnBlinkR.className = 'touch-blinker-btn';
    btnBlinkR.innerText = 'Blinker ➡️ [E]';
    btnBlinkR.style.cssText = blinkStyle;
    btnBlinkR.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(15);
      this.toggleBlinker('right');
    });

    const btnHorn = document.createElement('button');
    btnHorn.className = 'touch-btn-action';
    btnHorn.innerText = '📢 [T]';
    btnHorn.title = 'Hupe [T]';
    btnHorn.style.cssText = blinkStyle + ' color: #e2e8f0; border-color: #475569; padding: 6px 8px; font-size: 13px;';
    btnHorn.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(10);
      this.state.horn = true;
    });
    const releaseHorn = () => (this.state.horn = false);
    btnHorn.addEventListener('pointerup', releaseHorn);
    btnHorn.addEventListener('pointercancel', releaseHorn);

    blinkerRow.appendChild(btnBlinkL);
    blinkerRow.appendChild(btnHazard);
    blinkerRow.appendChild(btnBlinkR);
    blinkerRow.appendChild(btnHorn);

    // Steering Controls Container (Dynamic based on steeringMode)
    this.steerControlsContainer = document.createElement('div');
    this.steerControlsContainer.id = 'touch-steer-container';
    this.renderSteeringControls();

    this.leftThumbCluster.appendChild(blinkerRow);
    this.leftThumbCluster.appendChild(this.steerControlsContainer);

    // ── RIGHT CLUSTER: Pedals (Gas, Brake, Clutch) & Gear Controls ──
    this.rightThumbCluster = document.createElement('div');
    this.rightThumbCluster.id = 'touch-right-cluster';
    this.rightThumbCluster.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: flex-end;
      gap: 6px;
      pointer-events: auto;
    `;

    // Gear & Handbrake Bar (Above pedals)
    const gearHandbrakeBar = document.createElement('div');
    gearHandbrakeBar.style.cssText = `display: flex; gap: 6px; align-items: center;`;

    // Handbrake Button (🅿️)
    this.btnHandbrake = document.createElement('button');
    this.btnHandbrake.id = 'touch-btn-handbrake';
    this.btnHandbrake.className = 'touch-btn-action';
    this.btnHandbrake.innerHTML = '<span>🅿️</span><span>Handbremse [Space]</span>';
    this.btnHandbrake.style.cssText = `
      background: #991b1b;
      border: 1.5px solid #ef4444;
      color: white;
      padding: 6px 12px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 800;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
    `;
    this.btnHandbrake.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.triggerHaptic(25);
      this.state.handbrake = !this.state.handbrake;
      this.updateHandbrakeButtonUI(this.state.handbrake);
      if (this.onToggleHandbrakeCallback) {
        this.onToggleHandbrakeCallback(this.state.handbrake);
      }
    });

    // Quick D ⇄ R Gear Toggle Button
    this.btnQuickGear = document.createElement('button');
    this.btnQuickGear.id = 'touch-btn-quick-gear';
    this.btnQuickGear.className = 'touch-btn-action';
    const gearIcon = document.createElement('span');
    gearIcon.innerText = '🕹️';
    this.quickGearTextEl = document.createElement('span');
    this.quickGearTextEl.id = 'touch-quick-gear-text';
    this.quickGearTextEl.innerText = 'Gang: P [R]';
    this.btnQuickGear.appendChild(gearIcon);
    this.btnQuickGear.appendChild(this.quickGearTextEl);
    this.btnQuickGear.style.cssText = `
      background: #1e293b;
      border: 1.5px solid #3b82f6;
      color: #38bdf8;
      padding: 6px 12px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 800;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      font-family: 'Chakra Petch', sans-serif;
      box-shadow: 0 4px 12px rgba(0,0,0,0.5);
    `;
    this.btnQuickGear.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.triggerHaptic(20);
      this.toggleQuickGear();
    });

    gearHandbrakeBar.appendChild(this.btnHandbrake);
    gearHandbrakeBar.appendChild(this.btnQuickGear);

    // Pedals Row (Clutch [if manual], Brake, Gas)
    const pedalsRow = document.createElement('div');
    pedalsRow.id = 'touch-pedals-row';
    pedalsRow.style.cssText = `display: flex; gap: 8px; align-items: flex-end;`;

    // 1. Clutch Pedal (Shown in manual transmission only)
    this.clutchPedalEl = document.createElement('div');
    this.clutchPedalEl.id = 'touch-pedal-clutch';
    this.clutchPedalEl.className = 'touch-pedal';
    this.clutchPedalEl.style.cssText = `
      width: 50px;
      height: 98px;
      background: linear-gradient(180deg, #1e3a8a 0%, #172554 100%);
      border: 2px solid #3b82f6;
      border-radius: 12px;
      box-shadow: 0 6px 16px rgba(59, 130, 246, 0.3);
      display: none;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      color: white;
      font-weight: 800;
      font-size: 10px;
      cursor: pointer;
    `;
    this.clutchPedalEl.innerHTML = `<span>🕹️</span><span>KUPPLUNG</span>`;
    const handleClutchTouch = (down: boolean) => {
      this.state.clutch = down ? 1.0 : 0.0;
      if (this.clutchPedalEl) {
        if (down) {
          this.clutchPedalEl.classList.add('touch-pedal-pressed');
          this.triggerHaptic(15);
        } else {
          this.clutchPedalEl.classList.remove('touch-pedal-pressed');
        }
      }
    };
    this.clutchPedalEl.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.clutchPedalEl?.setPointerCapture(e.pointerId);
      handleClutchTouch(true);
    });
    this.clutchPedalEl.addEventListener('pointerup', (e) => {
      e.preventDefault();
      handleClutchTouch(false);
    });
    this.clutchPedalEl.addEventListener('pointercancel', (e) => {
      e.preventDefault();
      handleClutchTouch(false);
    });

    // 2. Brake Pedal (🛑 Wide pedal to the left of gas)
    this.brakePedalEl = document.createElement('div');
    this.brakePedalEl.id = 'touch-pedal-brake';
    this.brakePedalEl.className = 'touch-pedal';
    this.brakePedalEl.style.cssText = `
      width: 78px;
      height: 118px;
      background: linear-gradient(180deg, rgba(185, 28, 28, 0.85) 0%, rgba(127, 29, 29, 0.95) 100%);
      border: 2.5px solid #ef4444;
      border-radius: 14px;
      box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4), inset 0 2px 4px rgba(255,255,255,0.2);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      color: white;
      font-weight: 900;
      font-size: 13px;
      gap: 4px;
      cursor: pointer;
      touch-action: none;
      transition: transform 0.08s ease, filter 0.08s ease;
    `;
    this.brakePedalEl.innerHTML = `<span style="font-size:24px;">🛑</span><span>BREMSE</span>`;
    const handleBrakeTouch = (down: boolean) => {
      this.state.brake = down ? 1.0 : 0.0;
      if (this.brakePedalEl) {
        if (down) {
          this.brakePedalEl.classList.add('touch-pedal-pressed');
          this.triggerHaptic(12);
        } else {
          this.brakePedalEl.classList.remove('touch-pedal-pressed');
        }
      }
    };
    this.brakePedalEl.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.brakePedalEl.setPointerCapture(e.pointerId);
      handleBrakeTouch(true);
    });
    this.brakePedalEl.addEventListener('pointerup', (e) => {
      e.preventDefault();
      handleBrakeTouch(false);
    });
    this.brakePedalEl.addEventListener('pointercancel', (e) => {
      e.preventDefault();
      handleBrakeTouch(false);
    });

    // 3. Gas Pedal (⚡ Tall responsive pedal on the far right)
    this.gasPedalEl = document.createElement('div');
    this.gasPedalEl.id = 'touch-pedal-gas';
    this.gasPedalEl.className = 'touch-pedal';
    this.gasPedalEl.style.cssText = `
      width: 72px;
      height: 134px;
      background: linear-gradient(180deg, rgba(22, 163, 74, 0.85) 0%, rgba(20, 83, 45, 0.95) 100%);
      border: 2.5px solid #22c55e;
      border-radius: 14px;
      box-shadow: 0 6px 20px rgba(34, 197, 94, 0.4), inset 0 2px 4px rgba(255,255,255,0.2);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      color: white;
      font-weight: 900;
      font-size: 14px;
      gap: 6px;
      cursor: pointer;
      touch-action: none;
      transition: transform 0.08s ease, filter 0.08s ease;
    `;
    this.gasPedalEl.innerHTML = `<span style="font-size:26px;">⚡</span><span>GAS</span>`;
    const handleGasTouch = (down: boolean) => {
      this.state.throttle = down ? 1.0 : 0.0;
      if (this.gasPedalEl) {
        if (down) {
          this.gasPedalEl.classList.add('touch-pedal-pressed');
          this.triggerHaptic(12);
        } else {
          this.gasPedalEl.classList.remove('touch-pedal-pressed');
        }
      }
    };
    this.gasPedalEl.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      this.gasPedalEl.setPointerCapture(e.pointerId);
      handleGasTouch(true);
    });
    this.gasPedalEl.addEventListener('pointerup', (e) => {
      e.preventDefault();
      handleGasTouch(false);
    });
    this.gasPedalEl.addEventListener('pointercancel', (e) => {
      e.preventDefault();
      handleGasTouch(false);
    });

    pedalsRow.appendChild(this.clutchPedalEl);
    pedalsRow.appendChild(this.brakePedalEl);
    pedalsRow.appendChild(this.gasPedalEl);

    this.rightThumbCluster.appendChild(gearHandbrakeBar);
    this.rightThumbCluster.appendChild(pedalsRow);

    // Apply handedness (Standard: Left = Steer, Right = Pedals; Left-handed: Swapped)
    if (this.handedness === 'left') {
      this.bottomControlsBar.appendChild(this.rightThumbCluster);
      this.bottomControlsBar.appendChild(this.leftThumbCluster);
    } else {
      this.bottomControlsBar.appendChild(this.leftThumbCluster);
      this.bottomControlsBar.appendChild(this.rightThumbCluster);
    }

    this.container.appendChild(this.bottomControlsBar);
  }

  private toggleQuickGear() {
    if (this.currentTransmission === 'automatic') {
      // Toggle between D and R (or start in D from P)
      if (this.currentGearName === 'D' || this.currentGearName.startsWith('D')) {
        this.state.gearRequested = 'R';
      } else if (this.currentGearName === 'R') {
        this.state.gearRequested = 'D';
      } else {
        this.state.gearRequested = 'D';
      }
    } else {
      // In manual: toggle between 1st gear and Reverse
      if (this.currentGearName === 'R' || this.currentGearName === '-1') {
        this.state.gearRequested = '1';
      } else {
        this.state.gearRequested = '-1';
      }
    }
  }

  private renderSteeringControls() {
    if (!this.steerControlsContainer) return;
    this.steerControlsContainer.innerHTML = '';

    if (this.steeringMode === 'slider') {
      // ── MODE: Self-Centering Horizontal Steering Slider ──
      const sliderWrapper = document.createElement('div');
      sliderWrapper.style.cssText = `
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 3px;
        width: 170px;
        user-select: none;
        touch-action: none;
      `;

      const sliderTrack = document.createElement('div');
      sliderTrack.id = 'touch-steer-slider';
      sliderTrack.style.cssText = `
        position: relative;
        width: 164px;
        height: 52px;
        background: rgba(15, 23, 42, 0.90);
        border: 2px solid #38bdf8;
        border-radius: 26px;
        box-shadow: 0 4px 18px rgba(56, 189, 248, 0.35), inset 0 2px 6px rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        touch-action: none;
        cursor: grab;
        box-sizing: border-box;
      `;
      this.sliderTrackEl = sliderTrack;

      // Visual track labels & center notch
      const leftLabel = document.createElement('span');
      leftLabel.innerHTML = '◀ L';
      leftLabel.style.cssText = `
        position: absolute;
        left: 12px;
        font-size: 11px;
        font-weight: 800;
        color: #38bdf8;
        opacity: 0.8;
        pointer-events: none;
        user-select: none;
      `;

      const centerNotch = document.createElement('div');
      centerNotch.style.cssText = `
        position: absolute;
        left: 50%;
        top: 14px;
        width: 2px;
        height: 24px;
        background: rgba(148, 163, 184, 0.4);
        transform: translateX(-50%);
        pointer-events: none;
      `;

      const rightLabel = document.createElement('span');
      rightLabel.innerHTML = 'R ▶';
      rightLabel.style.cssText = `
        position: absolute;
        right: 12px;
        font-size: 11px;
        font-weight: 800;
        color: #38bdf8;
        opacity: 0.8;
        pointer-events: none;
        user-select: none;
      `;

      // Draggable thumb knob
      const thumb = document.createElement('div');
      thumb.id = 'touch-steer-slider-thumb';
      thumb.style.cssText = `
        position: absolute;
        left: 50%;
        top: 3px;
        width: 44px;
        height: 44px;
        margin-left: -22px;
        border-radius: 50%;
        background: radial-gradient(circle, #38bdf8 15%, #0284c7 85%);
        border: 2.5px solid #ffffff;
        box-shadow: 0 0 14px rgba(56, 189, 248, 0.75), 0 4px 10px rgba(0,0,0,0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 18px;
        color: white;
        touch-action: none;
        cursor: grab;
        transform: translateX(0px);
        transition: transform 0.18s cubic-bezier(0.175, 0.885, 0.32, 1.275);
      `;
      thumb.innerHTML = '↔️';
      this.sliderThumbEl = thumb;

      // Handle dragging
      const handleSliderMove = (clientX: number) => {
        const deltaX = clientX - this.sliderCenter;
        const clampedX = Math.max(-this.sliderMaxTravel, Math.min(this.sliderMaxTravel, deltaX));
        thumb.style.transform = `translateX(${clampedX}px)`;

        const norm = this.sliderMaxTravel > 0 ? clampedX / this.sliderMaxTravel : 0;
        this.sliderSteerTarget = Math.max(-1.0, Math.min(1.0, norm));
        this.state.steering = Math.sign(this.sliderSteerTarget) * Math.pow(Math.abs(this.sliderSteerTarget), 1.5);
      };

      const onPointerDown = (e: PointerEvent) => {
        if (typeof (e as any).preventDefault === 'function') (e as any).preventDefault();
        this.sliderTouchId = e.pointerId ?? 1;
        if (typeof (sliderTrack as any).setPointerCapture === 'function' && e.pointerId !== undefined) {
          try {
            sliderTrack.setPointerCapture(e.pointerId);
          } catch {}
        }
        thumb.style.transition = 'none'; // Instant drag response
        thumb.style.cursor = 'grabbing';
        sliderTrack.style.cursor = 'grabbing';

        const rect = sliderTrack.getBoundingClientRect ? sliderTrack.getBoundingClientRect() : { left: 0, width: 164 };
        this.sliderCenter = rect.left + rect.width / 2;
        this.sliderMaxTravel = (rect.width - 44) / 2;

        const clientX = e.clientX ?? (e as any).pageX ?? this.sliderCenter;
        handleSliderMove(clientX);
        this.triggerHaptic(12);
      };

      const onPointerMove = (e: PointerEvent) => {
        if (this.sliderTouchId === null) return;
        if (e.pointerId !== undefined && this.sliderTouchId !== e.pointerId) return;
        const clientX = e.clientX ?? (e as any).pageX ?? this.sliderCenter;
        handleSliderMove(clientX);
      };

      const onPointerRelease = (e: PointerEvent) => {
        if (this.sliderTouchId === null) return;
        if (e.pointerId !== undefined && this.sliderTouchId !== e.pointerId) return;
        this.sliderTouchId = null;
        thumb.style.cursor = 'grab';
        sliderTrack.style.cursor = 'grab';
        // Self-centering spring
        thumb.style.transition = 'transform 0.18s cubic-bezier(0.175, 0.885, 0.32, 1.275)';
        thumb.style.transform = 'translateX(0px)';
        this.sliderSteerTarget = 0;
        this.triggerHaptic(8);
      };

      sliderTrack.addEventListener('pointerdown', onPointerDown);
      sliderTrack.addEventListener('pointermove', onPointerMove);
      sliderTrack.addEventListener('pointerup', onPointerRelease);
      sliderTrack.addEventListener('pointercancel', onPointerRelease);

      thumb.addEventListener('pointerdown', onPointerDown);
      thumb.addEventListener('pointermove', onPointerMove);
      thumb.addEventListener('pointerup', onPointerRelease);
      thumb.addEventListener('pointercancel', onPointerRelease);

      sliderTrack.appendChild(leftLabel);
      sliderTrack.appendChild(centerNotch);
      sliderTrack.appendChild(rightLabel);
      sliderTrack.appendChild(thumb);
      sliderWrapper.appendChild(sliderTrack);

      const sliderSub = document.createElement('div');
      sliderSub.innerText = 'LENKUNG (FEDERND)';
      sliderSub.style.cssText = `
        font-size: 8.5px;
        font-weight: 800;
        color: #94a3b8;
        letter-spacing: 0.5px;
        text-transform: uppercase;
      `;
      sliderWrapper.appendChild(sliderSub);

      this.ensureArrowButtonFallbacks();
      this.steerControlsContainer.appendChild(sliderWrapper);

    } else if (this.steeringMode === 'arrows') {
      // ── MODE 1: Large comfortable tap arrows (◀ / ▶) ──
      const arrowsRow = document.createElement('div');
      arrowsRow.style.cssText = `display: flex; gap: 8px; align-items: center;`;

      const arrowBtnStyle = `
        width: 76px;
        height: 82px;
        background: rgba(15, 23, 42, 0.85);
        border: 2px solid #38bdf8;
        border-radius: 14px;
        color: #38bdf8;
        font-size: 32px;
        font-weight: 900;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        touch-action: none;
        box-shadow: 0 4px 16px rgba(56, 189, 248, 0.3);
        transition: transform 0.08s, background 0.08s;
      `;

      const btnLeft = document.createElement('button');
      btnLeft.id = 'touch-btn-steer-left';
      btnLeft.className = 'touch-steer-btn';
      btnLeft.innerHTML = `<span>◀</span><span style="font-size:10px;font-weight:700;margin-top:-4px;">LINKS</span>`;
      btnLeft.style.cssText = arrowBtnStyle;
      this.btnArrowLeft = btnLeft;

      btnLeft.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        this.arrowTouchLeftId = e.pointerId;
        btnLeft.setPointerCapture(e.pointerId);
        btnLeft.classList.add('touch-steer-pressed');
        this.arrowSteerTarget = -1.0;
        this.triggerHaptic(12);
      });
      const releaseLeft = (e: PointerEvent) => {
        if (this.arrowTouchLeftId === e.pointerId) {
          this.arrowTouchLeftId = null;
          btnLeft.classList.remove('touch-steer-pressed');
          if (this.arrowTouchRightId !== null) {
            this.arrowSteerTarget = 1.0;
          } else {
            this.arrowSteerTarget = 0;
          }
        }
      };
      btnLeft.addEventListener('pointerup', releaseLeft);
      btnLeft.addEventListener('pointercancel', releaseLeft);

      const btnRight = document.createElement('button');
      btnRight.id = 'touch-btn-steer-right';
      btnRight.className = 'touch-steer-btn';
      btnRight.innerHTML = `<span>▶</span><span style="font-size:10px;font-weight:700;margin-top:-4px;">RECHTS</span>`;
      btnRight.style.cssText = arrowBtnStyle;
      this.btnArrowRight = btnRight;

      btnRight.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        this.arrowTouchRightId = e.pointerId;
        btnRight.setPointerCapture(e.pointerId);
        btnRight.classList.add('touch-steer-pressed');
        this.arrowSteerTarget = 1.0;
        this.triggerHaptic(12);
      });
      const releaseRight = (e: PointerEvent) => {
        if (this.arrowTouchRightId === e.pointerId) {
          this.arrowTouchRightId = null;
          btnRight.classList.remove('touch-steer-pressed');
          if (this.arrowTouchLeftId !== null) {
            this.arrowSteerTarget = -1.0;
          } else {
            this.arrowSteerTarget = 0;
          }
        }
      };
      btnRight.addEventListener('pointerup', releaseRight);
      btnRight.addEventListener('pointercancel', releaseRight);

      arrowsRow.appendChild(btnLeft);
      arrowsRow.appendChild(btnRight);
      this.steerControlsContainer.appendChild(arrowsRow);

    } else if (this.steeringMode === 'wheel') {
      this.ensureArrowButtonFallbacks();
      // ── MODE 2: Virtual analog rotating steering wheel ──
      const wheelWrapper = document.createElement('div');
      wheelWrapper.style.cssText = `display: flex; align-items: center; justify-content: center; width: 140px; height: 120px;`;

      this.wheelElement = document.createElement('div');
      this.wheelElement.id = 'touch-steering-wheel';
      this.wheelElement.style.cssText = `
        width: 114px;
        height: 114px;
        border-radius: 50%;
        background: radial-gradient(circle, #1e293b 30%, #0f172a 70%);
        border: 8px solid #475569;
        box-shadow: 0 8px 24px rgba(0,0,0,0.7), inset 0 2px 6px rgba(255,255,255,0.2);
        display: flex;
        justify-content: center;
        align-items: center;
        position: relative;
        touch-action: none;
        user-select: none;
      `;
      this.wheelElement.innerHTML = `
        <div style="width: 8px; height: 100%; background: #475569; position: absolute;"></div>
        <div style="height: 8px; width: 100%; background: #475569; position: absolute;"></div>
        <div style="width: 36px; height: 36px; background: #2563eb; border-radius: 50%; z-index: 2; border: 2px solid white; display: flex; justify-content: center; align-items: center; font-size: 14px;">🚗</div>
      `;
      this.setupSteeringWheelTouch();

      wheelWrapper.appendChild(this.wheelElement);
      this.steerControlsContainer.appendChild(wheelWrapper);

    } else if (this.steeringMode === 'gyro') {
      // ── MODE 3: Gyroscope Tilt Steering with Live Meter ──
      const gyroBox = document.createElement('div');
      gyroBox.style.cssText = `
        width: 156px;
        background: rgba(15, 23, 42, 0.85);
        border: 1.5px solid #a78bfa;
        border-radius: 12px;
        padding: 8px 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 6px;
      `;

      gyroBox.innerHTML = `
        <div style="font-size: 10px; font-weight: 800; color: #c4b5fd; text-transform: uppercase;">
          📱 Neigungslenkung
        </div>
      `;

      // Live Level Meter Bar
      const meterTrack = document.createElement('div');
      meterTrack.style.cssText = `
        width: 100%;
        height: 12px;
        background: #1e293b;
        border-radius: 6px;
        position: relative;
        border: 1px solid #475569;
        overflow: hidden;
      `;
      const centerNotch = document.createElement('div');
      centerNotch.style.cssText = `
        position: absolute; left: 50%; top: 0; width: 2px; height: 100%; background: #94a3b8; transform: translateX(-50%);
      `;
      this.gyroIndicatorEl = document.createElement('div');
      this.gyroIndicatorEl.style.cssText = `
        position: absolute;
        top: 2px;
        left: 50%;
        width: 12px;
        height: 8px;
        background: #38bdf8;
        border-radius: 4px;
        transform: translateX(-50%);
        transition: left 0.05s linear;
      `;
      meterTrack.appendChild(centerNotch);
      meterTrack.appendChild(this.gyroIndicatorEl);
      gyroBox.appendChild(meterTrack);

      // Calibrate Button
      const btnCalibrate = document.createElement('button');
      btnCalibrate.innerHTML = '⌖ Nullpunkt kalibrieren';
      btnCalibrate.style.cssText = `
        background: #312e81;
        border: 1px solid #6366f1;
        color: white;
        padding: 4px 8px;
        border-radius: 6px;
        font-size: 10px;
        font-weight: 700;
        cursor: pointer;
      `;
      btnCalibrate.addEventListener('pointerdown', (e) => {
        e.preventDefault();
        this.triggerHaptic(20);
        this.gyroZeroAngle = this.gyroCurrentAngle;
      });
      gyroBox.appendChild(btnCalibrate);

      this.steerControlsContainer.appendChild(gyroBox);
    }
  }

  private setupSteeringWheelTouch() {
    if (!this.wheelElement) return;

    this.wheelElement.addEventListener('pointerdown', (e) => {
      e.preventDefault();
      const rect = this.wheelElement.getBoundingClientRect();
      this.wheelCenter = {
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2,
      };
      this.wheelTouchId = e.pointerId;
      this.wheelElement.setPointerCapture(e.pointerId);

      const dx = e.clientX - this.wheelCenter.x;
      const dy = e.clientY - this.wheelCenter.y;
      this.wheelStartAngle = Math.atan2(dy, dx) * (180 / Math.PI) - this.wheelCurrentRotation;
      this.triggerHaptic(10);
    });

    this.wheelElement.addEventListener('pointermove', (e) => {
      if (this.wheelTouchId !== e.pointerId) return;

      const dx = e.clientX - this.wheelCenter.x;
      const dy = e.clientY - this.wheelCenter.y;
      let angle = Math.atan2(dy, dx) * (180 / Math.PI) - this.wheelStartAngle;

      while (angle > 180) angle -= 360;
      while (angle < -180) angle += 360;
      this.wheelCurrentRotation = Math.max(-160, Math.min(160, angle));

      this.wheelElement.style.transform = `rotate(${this.wheelCurrentRotation}deg)`;
      const rawWheel = this.wheelCurrentRotation / 160;
      this.state.steering = Math.sign(rawWheel) * Math.pow(Math.abs(rawWheel), 1.65);
    });

    const releaseWheel = (e: PointerEvent) => {
      if (this.wheelTouchId === e.pointerId) {
        this.wheelTouchId = null;
      }
    };
    this.wheelElement.addEventListener('pointerup', releaseWheel);
    this.wheelElement.addEventListener('pointercancel', releaseWheel);
  }

  private setupGyro() {
    if (typeof window !== 'undefined' && 'DeviceOrientationEvent' in window) {
      window.addEventListener('deviceorientation', (e) => {
        if (!this.useGyroSteering || e.gamma === null) return;
        this.gyroCurrentAngle = e.gamma;
        const rawTilt = Math.max(-30, Math.min(30, this.gyroCurrentAngle - this.gyroZeroAngle));
        // Deadband of 1.2 deg for micro hand-tremor stabilization
        const deadband = 1.2;
        let normalizedTilt = 0;
        if (Math.abs(rawTilt) > deadband) {
          normalizedTilt = Math.sign(rawTilt) * ((Math.abs(rawTilt) - deadband) / (30 - deadband));
        }
        // Low-pass exponential smoothing
        this.filteredTilt += (normalizedTilt - this.filteredTilt) * 0.35;
        this.state.steering = Math.sign(this.filteredTilt) * Math.pow(Math.abs(this.filteredTilt), 1.65);

        if (this.gyroIndicatorEl) {
          const pct = ((rawTilt + 30) / 60) * 100;
          this.gyroIndicatorEl.style.left = `${Math.max(5, Math.min(95, pct))}%`;
        }
      });
    }
  }

  private updateHandbrakeButtonUI(latched: boolean) {
    if (!this.btnHandbrake) return;
    if (latched) {
      this.btnHandbrake.style.background = '#991b1b';
      this.btnHandbrake.style.borderColor = '#ef4444';
      this.btnHandbrake.innerHTML = '<span>🅿️</span><span>Handbremse [Space]</span>';
    } else {
      this.btnHandbrake.style.background = '#1e293b';
      this.btnHandbrake.style.borderColor = '#475569';
      this.btnHandbrake.innerHTML = '<span>🅿️</span><span>Gelöst [Space]</span>';
    }
  }

  /**
   * Updates touch button visual indicators based on vehicle telemetry
   */
  public updateTelemetry(telemetry: VehicleTelemetry, transmission: TransmissionType = 'automatic') {
    this.currentTransmission = transmission;
    this.currentGearName = telemetry.gear || 'P';

    // 1. Engine button styling
    if (this.btnEngine) {
      if (telemetry.engineRunning && !telemetry.isStalled) {
        this.btnEngine.style.background = '#15803d';
        this.btnEngine.style.borderColor = '#22c55e';
        this.btnEngine.innerHTML = '<span>🔑</span><span>Motor AN [I]</span>';
      } else if (telemetry.isStalled) {
        this.btnEngine.style.background = '#b91c1c';
        this.btnEngine.style.borderColor = '#ef4444';
        this.btnEngine.innerHTML = '<span>⚠️</span><span>Abgewürgt [I]</span>';
      } else {
        this.btnEngine.style.background = '#b45309';
        this.btnEngine.style.borderColor = '#f59e0b';
        this.btnEngine.innerHTML = '<span>🔑</span><span>Starten [I]</span>';
      }
    }

    // 2. Seatbelt button styling
    if (this.btnSeatbelt) {
      if (telemetry.seatbeltFastened) {
        this.btnSeatbelt.style.background = '#15803d';
        this.btnSeatbelt.style.borderColor = '#22c55e';
        this.btnSeatbelt.innerHTML = '<span>🔒</span><span>Angeschnallt [B]</span>';
      } else {
        this.btnSeatbelt.style.background = '#991b1b';
        this.btnSeatbelt.style.borderColor = '#ef4444';
        this.btnSeatbelt.innerHTML = '<span>🔴</span><span>Anschnallen [B]</span>';
      }
    }

    // 3. Handbrake button styling
    this.updateHandbrakeButtonUI(telemetry.handbrake);

    // 4. Quick Gear Display with live speed
    const gearTextEl = this.quickGearTextEl || document.getElementById('touch-quick-gear-text');
    if (gearTextEl) {
      const spd = Math.round(Math.abs(telemetry.speedKmh));
      gearTextEl.innerText = `Gang: ${this.currentGearName} • ${spd} km/h`;
    }
    if (this.btnQuickGear) {
      if (this.currentGearName.startsWith('D')) {
        this.btnQuickGear.style.background = '#15803d';
        this.btnQuickGear.style.borderColor = '#22c55e';
        this.btnQuickGear.style.color = '#ffffff';
      } else if (this.currentGearName === 'R') {
        this.btnQuickGear.style.background = '#b45309';
        this.btnQuickGear.style.borderColor = '#f59e0b';
        this.btnQuickGear.style.color = '#ffffff';
      } else {
        this.btnQuickGear.style.background = '#1e293b';
        this.btnQuickGear.style.borderColor = '#3b82f6';
        this.btnQuickGear.style.color = '#38bdf8';
      }
    }

    // 5. Show/hide clutch pedal depending on transmission
    if (this.clutchPedalEl) {
      this.clutchPedalEl.style.display = transmission === 'manual' ? 'flex' : 'none';
    }
  }

  public setHandedness(handedness: MobileHandedness) {
    this.handedness = handedness;
    this.savePreferences();
    if (this.bottomControlsBar && this.leftThumbCluster && this.rightThumbCluster) {
      this.bottomControlsBar.innerHTML = '';
      if (this.handedness === 'left') {
        this.bottomControlsBar.appendChild(this.rightThumbCluster);
        this.bottomControlsBar.appendChild(this.leftThumbCluster);
      } else {
        this.bottomControlsBar.appendChild(this.leftThumbCluster);
        this.bottomControlsBar.appendChild(this.rightThumbCluster);
      }
    }
  }

  public setButtonScale(scale: MobileButtonScale) {
    this.buttonScale = scale;
    this.savePreferences();
    const isLarge = scale === 'large';
    if (this.gasPedalEl) {
      this.gasPedalEl.style.width = isLarge ? '84px' : '72px';
      this.gasPedalEl.style.height = isLarge ? '146px' : '134px';
    }
    if (this.brakePedalEl) {
      this.brakePedalEl.style.width = isLarge ? '90px' : '78px';
      this.brakePedalEl.style.height = isLarge ? '130px' : '118px';
    }
  }

  public setHapticEnabled(enabled: boolean) {
    this.hapticEnabled = enabled;
    this.savePreferences();
  }

  public consumeCameraToggle(): boolean {
    const val = this.state.cameraToggle;
    this.state.cameraToggle = false;
    return val;
  }

  public consumeEngineRestart(): boolean {
    const val = this.state.engineRestart;
    this.state.engineRestart = false;
    return val;
  }

  public consumeSeatbeltToggle(): boolean {
    const val = this.state.seatbeltToggle;
    this.state.seatbeltToggle = false;
    return val;
  }

  public consumeGearShiftUp(): boolean {
    const val = this.state.gearShiftUp;
    this.state.gearShiftUp = false;
    return val;
  }

  public consumeGearShiftDown(): boolean {
    const val = this.state.gearShiftDown;
    this.state.gearShiftDown = false;
    return val;
  }

  public consumeGearRequested(): string | null {
    const val = this.state.gearRequested;
    this.state.gearRequested = null;
    return val;
  }

  private ensureArrowButtonFallbacks() {
    if (!this.btnArrowLeft) {
      this.btnArrowLeft = document.createElement('button');
      this.btnArrowLeft.id = 'touch-btn-steer-left';
      this.btnArrowLeft.className = 'touch-steer-btn';
      this.btnArrowLeft.style.display = 'none';
      this.btnArrowLeft.addEventListener('pointerdown', (e: any) => {
        this.arrowSteerTarget = -1.0;
      });
      const releaseL = () => {
        this.arrowSteerTarget = 0;
      };
      this.btnArrowLeft.addEventListener('pointerup', releaseL);
      this.btnArrowLeft.addEventListener('pointercancel', releaseL);
    }
    if (!this.btnArrowRight) {
      this.btnArrowRight = document.createElement('button');
      this.btnArrowRight.id = 'touch-btn-steer-right';
      this.btnArrowRight.className = 'touch-steer-btn';
      this.btnArrowRight.style.display = 'none';
      this.btnArrowRight.addEventListener('pointerdown', (e: any) => {
        this.arrowSteerTarget = 1.0;
      });
      const releaseR = () => {
        this.arrowSteerTarget = 0;
      };
      this.btnArrowRight.addEventListener('pointerup', releaseR);
      this.btnArrowRight.addEventListener('pointercancel', releaseR);
    }
  }

  public update(deltaTime: number) {
    // 0. Self-Centering Slider Steering
    if (this.steeringMode === 'slider') {
      if (this.sliderTouchId !== null) {
        this.state.steering = Math.sign(this.sliderSteerTarget) * Math.pow(Math.abs(this.sliderSteerTarget), 1.5);
      } else if (Math.abs(this.arrowSteerTarget) > 0.01) {
        // Fallback compatibility if arrow target is manipulated
        this.state.steering += (this.arrowSteerTarget - this.state.steering) * Math.min(1.0, deltaTime * 8.0);
      } else {
        // Self-centering spring decay (snappy mechanical return)
        this.state.steering += (0 - this.state.steering) * Math.min(1.0, deltaTime * 18.0);
        if (Math.abs(this.state.steering) < 0.01) {
          this.state.steering = 0;
        }
      }
    }

    // 1. Arrow Steering: Progressive two-phase hesitant ramp & gamma curve
    if (this.steeringMode === 'arrows') {
      const steerApplying = Math.abs(this.arrowSteerTarget) > 0.01;
      if (steerApplying) {
        if (Math.sign(this.arrowSteerTarget) !== Math.sign(this.lastArrowSteerTarget)) {
          this.arrowSteerHoldDuration = 0;
          this.rawSteering = 0;
        }
        this.arrowSteerHoldDuration += deltaTime;
        this.lastArrowSteerTarget = this.arrowSteerTarget;

        const onsetProgress = Math.min(1.0, this.arrowSteerHoldDuration / 0.22);
        const activeRate = 2.8 + onsetProgress * 3.4;
        this.rawSteering += (this.arrowSteerTarget - this.rawSteering) * Math.min(1.0, deltaTime * activeRate);

        // Apply non-linear gamma curve (gamma = 1.65)
        const steerSign = Math.sign(this.rawSteering);
        const steerMag = Math.abs(this.rawSteering);
        this.state.steering = steerSign * Math.pow(steerMag, 1.65);
      } else {
        this.arrowSteerHoldDuration = 0;
        this.lastArrowSteerTarget = 0;
        this.state.steering += (0 - this.state.steering) * Math.min(1.0, deltaTime * 7.5);
        if (Math.abs(this.state.steering) < 0.005) {
          this.state.steering = 0;
          this.rawSteering = 0;
        } else {
          const s = Math.sign(this.state.steering);
          this.rawSteering = s * Math.pow(Math.abs(this.state.steering), 1 / 1.65);
        }
      }
    }

    // 2. Virtual Steering Wheel: Damped spring return with gamma curve
    if (this.steeringMode === 'wheel' && this.wheelTouchId === null && !this.useGyroSteering) {
      this.wheelCurrentRotation += (0 - this.wheelCurrentRotation) * Math.min(1.0, deltaTime * 8.5);
      if (Math.abs(this.wheelCurrentRotation) < 0.5) this.wheelCurrentRotation = 0;
      if (this.wheelElement) {
        this.wheelElement.style.transform = `rotate(${this.wheelCurrentRotation}deg)`;
      }
      const rawWheel = this.wheelCurrentRotation / 160;
      this.state.steering = Math.sign(rawWheel) * Math.pow(Math.abs(rawWheel), 1.65);
    }
  }

  public mount(root: HTMLElement) {
    root.appendChild(this.container);
    if (this.rotateOverlay && this.rotateOverlay.parentElement !== document.body) {
      if (typeof document !== 'undefined' && document.body) {
        document.body.appendChild(this.rotateOverlay);
      } else {
        root.appendChild(this.rotateOverlay);
      }
    }
  }
}
